import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosInstance";

function formatDate(dateValue) {
  if (!dateValue) return "Not applied yet";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "Invalid date";
  return parsed.toLocaleString();
}

function formatOccupancy(row) {
  const parts = [];
  if (row.adults !== null && typeof row.adults !== "undefined") {
    parts.push(`Adults ${row.adults} (${row.adults_match_type || "exact"})`);
  }
  if (row.children !== null && typeof row.children !== "undefined") {
    parts.push(`Children ${row.children} (${row.children_match_type || "upto"})`);
  }
  if (row.max_occupancy !== null && typeof row.max_occupancy !== "undefined") {
    parts.push(`Max pax ${row.max_occupancy} (${row.max_occupancy_match_type || "upto"})`);
  }
  return parts.length ? parts.join(" · ") : "Open occupancy";
}

function formatStayDefaults(row) {
  const minStay = row.default_min_stay ?? null;
  const maxStay = row.default_max_stay ?? null;
  const weekdayMin = row.default_min_stay_by_weekday && typeof row.default_min_stay_by_weekday === "object";
  const weekdayMax = row.default_max_stay_by_weekday && typeof row.default_max_stay_by_weekday === "object";

  if (weekdayMin || weekdayMax) return "Weekday rules";
  if (minStay === null && maxStay === null) return "No default stay";
  return `${minStay ?? "-"} / ${maxStay ?? "-"}`;
}

export default function OnboardingRoomRatesAdmin() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [applyingBusinessUnitId, setApplyingBusinessUnitId] = useState(null);
  const [feedback, setFeedback] = useState("");

  const selectedRows = useMemo(
    () => (Array.isArray(selectedDraft?.rows) ? selectedDraft.rows : []),
    [selectedDraft?.rows]
  );
  const selectedSummary = useMemo(
    () => ({
      total: selectedRows.length,
      linked: selectedRows.filter((row) => row.applied_room_rate_id).length,
      visible: selectedRows.filter((row) => row.show_in_booking_engine !== false).length,
      refundable: selectedRows.filter((row) => row.is_refundable !== false).length,
    }),
    [selectedRows]
  );

  const loadDrafts = async () => {
    setLoading(true);
    setFeedback("");

    try {
      const response = await axios.get("/api/onboarding/admin/room-rates/drafts");
      setDrafts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("[Admin][Onboarding][RoomRates] Error loading drafts:", error);
      setFeedback("Could not load onboarding room rate drafts.");
    } finally {
      setLoading(false);
    }
  };

  const loadDraftDetail = async (businessUnitId) => {
    setSelectedBusinessUnitId(businessUnitId);
    setDetailLoading(true);
    setFeedback("");

    try {
      const response = await axios.get(`/api/onboarding/admin/room-rates/${businessUnitId}`);
      setSelectedDraft(response.data);
    } catch (error) {
      console.error("[Admin][Onboarding][RoomRates] Error loading draft detail:", error);
      setSelectedDraft(null);
      setFeedback("Could not load the selected room rate draft.");
    } finally {
      setDetailLoading(false);
    }
  };

  const applyDraft = async (businessUnitId, businessUnitName) => {
    const confirmed = window.confirm(
      `Apply the onboarding room rates draft for ${businessUnitName}? This will create or update rows in room_rates.`
    );

    if (!confirmed) return;

    setApplyingBusinessUnitId(businessUnitId);
    setFeedback("");

    try {
      const response = await axios.post(`/api/onboarding/admin/room-rates/${businessUnitId}/apply`);
      const result = response.data?.result || {};
      setFeedback(
        `Draft applied for ${businessUnitName}. Created: ${result.created || 0}. Updated: ${result.updated || 0}.`
      );
      await loadDrafts();
      await loadDraftDetail(businessUnitId);
    } catch (error) {
      console.error("[Admin][Onboarding][RoomRates] Error applying draft:", error);
      setFeedback(error?.response?.data?.error || "Could not apply the selected room rate draft.");
    } finally {
      setApplyingBusinessUnitId(null);
    }
  };

  useEffect(() => {
    loadDrafts();
  }, []);

  return (
    <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "18px" }}>
      <div
        style={{
          borderRadius: "22px",
          padding: "22px",
          border: "1px solid rgba(255,255,255,0.08)",
          background:
            "radial-gradient(780px 240px at 0% 0%, rgba(92,191,255,0.12), transparent 60%), rgba(10,15,28,0.9)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
        }}
      >
        <div style={{ fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.12em", color: "#8ea0bc" }}>
          Admin apply area
        </div>
        <h2 style={{ color: "#fff", margin: "8px 0 10px" }}>Onboarding Room Rates</h2>
        <p style={{ color: "#b9c3d7", maxWidth: "920px", margin: 0, lineHeight: 1.6 }}>
          Rates created in onboarding stay staged until an admin reviews them here and applies them to{" "}
          <strong>`room_rates`</strong>. Theme and language are the only onboarding choices that bypass this review.
        </p>
      </div>

      {feedback ? (
        <div
          style={{
            borderRadius: "14px",
            padding: "12px 14px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: feedback.toLowerCase().includes("could not") || feedback.toLowerCase().includes("must")
              ? "#542929"
              : "#244428",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {feedback}
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(320px, 420px) minmax(0, 1fr)", gap: "18px" }}>
        <section
          style={{
            borderRadius: "20px",
            padding: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.9)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
            <div>
              <h3 style={{ color: "#fff", margin: 0 }}>Drafts</h3>
              <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>Business units with staged room rate drafts.</p>
            </div>
            <button
              type="button"
              onClick={loadDrafts}
              disabled={loading}
              style={{
                borderRadius: "12px",
                border: "none",
                padding: "10px 14px",
                background: "#5cbfff",
                color: "#08111e",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
            {loading ? (
              <div style={{ color: "#9db0cf" }}>Loading room rate drafts...</div>
            ) : drafts.length === 0 ? (
              <div style={{ color: "#9db0cf" }}>No room rate drafts available yet.</div>
            ) : (
              drafts.map((draft) => {
                const isSelected = Number(selectedBusinessUnitId) === Number(draft.business_unit_id);
                return (
                  <article
                    key={draft.business_unit_id}
                    style={{
                      borderRadius: "18px",
                      padding: "16px",
                      border: `1px solid ${isSelected ? "rgba(92,191,255,0.45)" : "rgba(255,255,255,0.08)"}`,
                      background: isSelected ? "rgba(92,191,255,0.08)" : "rgba(255,255,255,0.03)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                      <div>
                        <h4 style={{ color: "#fff", margin: 0 }}>{draft.business_unit_name}</h4>
                        <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>{draft.rows_total} staged rates</p>
                      </div>
                      <div style={{ color: "#e7edf8", fontWeight: 700 }}>{draft.linked_rows_total} linked</div>
                    </div>

                    <div style={{ marginTop: "12px", color: "#9db0cf", fontSize: "0.9rem", lineHeight: 1.5 }}>
                      <div>Last save: {formatDate(draft.updated_at)}</div>
                      <div>Last apply: {formatDate(draft.applied_at)}</div>
                    </div>

                    <div style={{ marginTop: "14px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => loadDraftDetail(draft.business_unit_id)}
                        style={{
                          borderRadius: "12px",
                          border: "none",
                          padding: "10px 14px",
                          background: "#1b2c43",
                          color: "#fff",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => applyDraft(draft.business_unit_id, draft.business_unit_name)}
                        disabled={applyingBusinessUnitId === draft.business_unit_id}
                        style={{
                          borderRadius: "12px",
                          border: "none",
                          padding: "10px 14px",
                          background: "#ff975c",
                          color: "#08111e",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {applyingBusinessUnitId === draft.business_unit_id ? "Applying..." : "Apply to live"}
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </section>

        <section
          style={{
            borderRadius: "20px",
            padding: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.9)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          }}
        >
          {!selectedBusinessUnitId ? (
            <div style={{ color: "#9db0cf" }}>Select a draft to preview its staged room rates.</div>
          ) : detailLoading ? (
            <div style={{ color: "#9db0cf" }}>Loading draft detail...</div>
          ) : !selectedDraft ? (
            <div style={{ color: "#9db0cf" }}>No draft detail loaded.</div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "14px", flexWrap: "wrap", marginBottom: "16px" }}>
                <div>
                  <h3 style={{ color: "#fff", margin: 0 }}>{selectedDraft.session?.business_unit_name}</h3>
                  <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>
                    Last save: {formatDate(selectedDraft.session?.updated_at)}. Last apply:{" "}
                    {formatDate(selectedDraft.session?.applied_at)}.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    applyDraft(selectedDraft.session.business_unit_id, selectedDraft.session.business_unit_name)
                  }
                  disabled={applyingBusinessUnitId === selectedDraft.session?.business_unit_id}
                  style={{
                    borderRadius: "12px",
                    border: "none",
                    padding: "12px 16px",
                    background: "#5cbfff",
                    color: "#08111e",
                    fontWeight: 800,
                    cursor: "pointer",
                    opacity: applyingBusinessUnitId === selectedDraft.session?.business_unit_id ? 0.7 : 1,
                  }}
                >
                  {applyingBusinessUnitId === selectedDraft.session?.business_unit_id ? "Applying..." : "Apply draft"}
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px", marginBottom: "16px" }}>
                {[
                  ["Rates", selectedSummary.total],
                  ["Linked", selectedSummary.linked],
                  ["Booking Engine visible", selectedSummary.visible],
                  ["Refundable", selectedSummary.refundable],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      borderRadius: "14px",
                      border: "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.03)",
                      padding: "14px",
                    }}
                  >
                    <div style={{ color: "#8ea0bc", fontSize: "0.82rem" }}>{label}</div>
                    <div style={{ color: "#fff", fontSize: "1.5rem", fontWeight: 800, marginTop: "6px" }}>{value}</div>
                  </div>
                ))}
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff" }}>
                  <thead>
                    <tr style={{ textAlign: "left", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                      <th style={{ padding: "10px 8px" }}>Category</th>
                      <th style={{ padding: "10px 8px" }}>Code</th>
                      <th style={{ padding: "10px 8px" }}>Name</th>
                      <th style={{ padding: "10px 8px" }}>Currency</th>
                      <th style={{ padding: "10px 8px" }}>Occupancy</th>
                      <th style={{ padding: "10px 8px" }}>Stay defaults</th>
                      <th style={{ padding: "10px 8px" }}>Live row</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRows.map((row) => (
                      <tr key={row.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                        <td style={{ padding: "10px 8px", fontWeight: 700 }}>
                          {row.onboarding_room_category?.name || "-"}
                        </td>
                        <td style={{ padding: "10px 8px" }}>{row.rate_code}</td>
                        <td style={{ padding: "10px 8px", color: "#c3cee0" }}>{row.name}</td>
                        <td style={{ padding: "10px 8px" }}>{row.currency || "-"}</td>
                        <td style={{ padding: "10px 8px", color: "#c3cee0" }}>{formatOccupancy(row)}</td>
                        <td style={{ padding: "10px 8px", color: "#c3cee0" }}>{formatStayDefaults(row)}</td>
                        <td style={{ padding: "10px 8px" }}>{row.applied_room_rate_id || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
